import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthedImage from "@/components/AuthedImage";
import { toast } from "@/components/ui/sonner";
import { Camera, Globe, Lock, Settings, Upload, Save, Bell } from "lucide-react";
import SocialLinks, { SOCIAL_PLATFORMS } from "@/components/SocialLinks";
import HScroll from "@/components/HScroll";
import HeroEditor from "@/components/HeroEditor";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

const FIELDS = [
  ["name", "Full name"],
  ["city", "City"],
  ["state", "State"],
  ["other_places", "Other places (comma separated)"],
  ["phone", "Phone"],
  ["high_school", "High school(s) (comma separated)"],
  ["college", "College(s) (comma separated)"],
  ["bio", "Bio"],
];

const asCsv = (v) => (Array.isArray(v) ? v.join(", ") : (v || ""));
const toList = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);

export default function Profile() {
  const { screenName } = useParams();
  const { user, refreshMe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [socials, setSocials] = useState({});
  const [privacy, setPrivacy] = useState({});
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyFrequency, setNotifyFrequency] = useState("instant");
  const [hero, setHero] = useState({ fit: "contain", pos: "50% 50%" });
  const [saving, setSaving] = useState(false);

  const isMe = user && typeof user === "object" && user.screen_name?.toLowerCase() === screenName.toLowerCase();

  const reload = useCallback(async () => {
    const { data } = await api.get(`/profile/${screenName}`);
    setProfile(data);
    setForm({
      name: data.name || "",
      city: data.city || "",
      state: data.state || "",
      other_places: (data.other_places || []).join(", "),
      phone: data.phone || "",
      high_school: asCsv(data.high_school),
      college: asCsv(data.college),
      bio: data.bio || "",
    });
    setSocials(data.socials || {});
    setPrivacy(data.privacy || {});
    setNotifyEmail(!!data.notify_email);
    setNotifyFrequency(data.notify_frequency || "instant");
    setHero({ fit: data.hero_fit || "contain", pos: data.hero_pos || "50% 50%" });
  }, [screenName]);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        city: form.city || null,
        state: form.state || null,
        other_places: form.other_places ? form.other_places.split(",").map((s) => s.trim()).filter(Boolean) : [],
        socials,
        phone: form.phone || null,
        high_school: toList(form.high_school),
        college: toList(form.college),
        bio: form.bio || null,
        privacy,
        notify_email: notifyEmail,
        notify_frequency: notifyFrequency,
        hero_fit: hero.fit,
        hero_pos: hero.pos,
      };
      await api.patch("/profile", payload);
      await refreshMe();
      await reload();
      setEditing(false);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save profile");
    } finally { setSaving(false); }
  };

  const clearProfileHero = async () => {
    if (!confirm("Clear your profile hero photo?")) return;
    await api.patch("/profile", { profile_cover_photo_id: "" });
    await reload();
  };

  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/api/share/u/${screenName}`;
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  if (!profile) return <div className="max-w-4xl mx-auto px-6 py-16 text-[#8A8A8A]">Loading...</div>;

  return (
    <div className="paper-grain min-h-screen" data-testid="profile-page">
      {profile.profile_cover_photo_id && (
        <div
          className="relative h-64 sm:h-80 lg:h-96 bg-[#1F1F1F] overflow-hidden"
          data-testid="profile-hero-banner"
        >
          <AuthedImage
            photoId={profile.profile_cover_photo_id}
            variant="hero"
            fit={profile.hero_fit || "contain"}
            position={profile.hero_pos || "50% 50%"}
          />
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)" }}
          />
          <div className="absolute bottom-6 left-0 right-0 px-6 max-w-5xl mx-auto z-10">
            <p className="font-script text-3xl text-[#F9F6F0] drop-shadow">@{profile.screen_name}</p>
            <h1
              className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F9F6F0] drop-shadow"
              data-testid="profile-hero-name"
            >
              {profile.name || profile.screen_name}
            </h1>
            <SocialLinks socials={profile.socials} tone="onHero" label="" className="mt-3" testId="profile-hero-socials"/>
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
          {!profile.profile_cover_photo_id && (
            <div>
              <p className="font-script text-2xl text-[#8C3A3A]">@{profile.screen_name}</p>
              <h1 className="font-serif-display text-5xl" data-testid="profile-display-name">{profile.name || profile.screen_name}</h1>
            </div>
          )}
          {profile.profile_cover_photo_id && (
            <div className="grow"/>
          )}
          <div className="flex gap-2 items-center">
            {!isMe && user && typeof user === "object" && (
              <Link to={`/post?to=${profile.screen_name}`} className="btn-primary" data-testid="profile-post-btn">
                <Upload className="w-4 h-4"/> Post to their album
              </Link>
            )}
            {isMe && !editing && (
              <>
                {profile.profile_cover_photo_id && (
                  <button
                    onClick={clearProfileHero}
                    className="btn-ghost text-sm"
                    data-testid="profile-clear-hero-btn"
                    title="Remove profile hero photo"
                  >
                    Clear hero
                  </button>
                )}
                <button onClick={() => setEditing(true)} className="btn-secondary" data-testid="profile-edit-btn">
                  <Settings className="w-4 h-4"/> Edit profile
                </button>
              </>
            )}
            {isMe && editing && (
              <>
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="profile-save-btn">
                  <Save className="w-4 h-4"/> {saving ? "..." : "Save"}
                </button>
                <button onClick={() => setEditing(false)} className="btn-ghost" data-testid="profile-cancel-btn">Cancel</button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="paper-card p-6 mt-6">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-4">Profile fields</p>
            <p className="text-xs text-[#8A8A8A] mb-6">Check the "searchable" box to let others find you by that field. Your screen name is always searchable.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label className="block text-xs uppercase tracking-[0.18em] font-semibold mb-2">{label}</label>
                  {key === "bio" ? (
                    <textarea data-testid={`profile-input-${key}`} className="mp-input" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}/>
                  ) : (
                    <input data-testid={`profile-input-${key}`} className="mp-input" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}/>
                  )}
                  <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
                    <input data-testid={`privacy-toggle-${key}`} type="checkbox" checked={!!privacy[key]} onChange={(e) => setPrivacy({ ...privacy, [key]: e.target.checked })}/>
                    Searchable by others
                  </label>
                </div>
              ))}
            </div>

            {/* Hero display */}
            {profile.profile_cover_photo_id && (
              <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="hero-display-section">
                <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-2">Hero photo display</p>
                <p className="text-xs text-[#8A8A8A] mb-4">Choose how your banner photo fills the space at the top of your profile.</p>
                <HeroEditor
                  photoId={profile.profile_cover_photo_id}
                  fit={hero.fit}
                  pos={hero.pos}
                  onChange={setHero}
                />
              </div>
            )}

            {/* Social handles section */}
            <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="socials-edit-section">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-2">Social handles</p>
              <p className="text-xs text-[#8A8A8A] mb-4">Add just your username for each — we'll turn it into a link. Leave blank to skip.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SOCIAL_PLATFORMS.map(({ key, label, Icon, color, placeholder }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#F0EBE1] rounded-full">
                      <Icon style={{ color }} className="w-4 h-4"/>
                    </span>
                    <input
                      data-testid={`social-input-${key}`}
                      className="mp-input"
                      placeholder={`${label} ${placeholder}`}
                      value={socials[key] || ""}
                      onChange={(e) => setSocials({ ...socials, [key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-4 text-xs cursor-pointer">
                <input data-testid="privacy-toggle-socials" type="checkbox" checked={!!privacy.socials} onChange={(e) => setPrivacy({ ...privacy, socials: e.target.checked })}/>
                Make my socials searchable / visible to others
              </label>
            </div>

            {/* Notifications section */}
            <div className="mt-10 pt-8 border-t border-[#E5E0D8]">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-4 h-4 text-[#8C3A3A]"/>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold">Email notifications</p>
              </div>
              <p className="text-xs text-[#8A8A8A] mb-4">
                Get an email when a friend uploads a photo of you. We'll only email the address on your account.
              </p>
              <label className="flex items-center gap-3 mb-4 cursor-pointer" data-testid="notify-toggle-label">
                <input
                  data-testid="notify-email-toggle"
                  type="checkbox"
                  checked={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)}
                />
                <span className="text-sm">Email me about new photos</span>
              </label>
              {notifyEmail && (
                <div className="ml-7 space-y-2">
                  <p className="text-xs uppercase tracking-[0.14em] text-[#5C5C5C]">Frequency</p>
                  {[
                    ["instant", "Instant — one email per photo"],
                    ["daily", "Daily digest — one email per day with a summary"],
                    ["off", "Off — no emails (same as unchecking above)"],
                  ].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        data-testid={`notify-freq-${val}`}
                        type="radio"
                        name="notify_frequency"
                        value={val}
                        checked={notifyFrequency === val}
                        onChange={() => setNotifyFrequency(val)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Danger zone */}
            <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="danger-zone">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-2 text-[#8C3A3A]">Danger zone</p>
              <p className="text-xs text-[#8A8A8A] mb-4">
                Deleting your account permanently removes your profile, albums, and every photo of you.
                Photos you posted of other people remain with them. This can't be undone.
              </p>
              <DeleteAccountDialog />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <div className="paper-card p-6 lg:col-span-1">
              <p className="text-xs uppercase tracking-[0.18em] font-semibold mb-4">About</p>
              <ProfileField label="Name" value={profile.name}/>
              <ProfileField label="From" value={[profile.city, profile.state].filter(Boolean).join(", ")}/>
              <ProfileField label="Other places" value={(profile.other_places || []).join(", ")}/>
              <ProfileField label="High school" value={asCsv(profile.high_school)}/>
              <ProfileField label="College" value={asCsv(profile.college)}/>
              <SocialLinks socials={profile.socials} legacy={profile.social_handles} className="mb-3"/>
              <ProfileField label="Phone" value={profile.phone}/>
              {profile.bio && <p className="text-[#1F1F1F] italic mt-4 leading-relaxed">"{profile.bio}"</p>}
            </div>

            <div className="lg:col-span-2">
              <div className="flex items-baseline gap-3 mb-4">
                <h2 className="font-serif-display text-3xl">Public albums</h2>
                <span className="text-[#8A8A8A] text-sm">{(profile.public_albums || []).length}</span>
              </div>
              {(profile.public_albums || []).length === 0 ? (
                <div className="paper-card p-12 text-center">
                  <p className="font-script text-2xl text-[#5C5C5C]">no public albums yet</p>
                </div>
              ) : (
                <HScroll testId="profile-albums-carousel">
                  {profile.public_albums.map((a) => (
                    <Link key={a.id} to={`/album/${a.id}`} className="paper-card p-4 block hover:-translate-y-1 transition-transform w-60 shrink-0 snap-start" data-testid={`profile-album-${a.id}`}>
                      <div className="aspect-square bg-[#F0EBE1] mb-4 flex items-center justify-center text-[#8A8A8A] overflow-hidden">
                        {a.cover_photo_id ? (
                          <AuthedImage photoId={a.cover_photo_id} className="w-full h-full object-cover"/>
                        ) : (
                          <Camera className="w-12 h-12 opacity-30"/>
                        )}
                      </div>
                      <h3 className="font-serif-display text-xl mb-1">{a.title}</h3>
                      <p className="text-xs text-[#5C5C5C] flex items-center gap-2">
                        <Globe className="w-3 h-3"/> Public · {a.photo_count} photos
                      </p>
                    </Link>
                  ))}
                </HScroll>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileField({ label, value }) {
  if (!value) return null;
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</p>
      <p className="text-[#1F1F1F]">{value}</p>
    </div>
  );
}
